// src/admin/hooks/useAdmins.ts
// Reads the admin_users table. RLS already filters to active admins the
// caller is permitted to see — in v1 every admin role can read the full
// list; only super_admin can mutate via the modals on AdminsListScreen /
// AdminDetailScreen.
//
// Schema note: `last_login_ip` and `deleted_at` are in the underlying
// Row type but we don't need them here. Selecting only the columns we
// render keeps the PostgREST payload small and makes the shape of
// AdminRow explicit for downstream screens.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';

// Matches the enum in database.types (admin_role, not admin_role_enum as
// the plan suggested — the generator emits the actual pg enum name).
export type AdminRole = Database['public']['Enums']['admin_role'];

export interface AdminRow {
  id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  is_readonly: boolean;
  is_active: boolean;
  last_login_at: string | null;
  totp_enrolled_at: string | null;
}

export function useAdmins() {
  return useQuery<AdminRow[]>({
    queryKey: ['admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_users')
        .select(
          'id, email, display_name, role, is_readonly, is_active, last_login_at, totp_enrolled_at',
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AdminRow[];
    },
  });
}
